import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Tabs,
  Table,
  Form,
  InputNumber,
  Switch,
  message,
  Space,
  Popconfirm,
} from 'antd'
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  PoweroffOutlined,
  CloudUploadOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import type { TabsProps } from 'antd'
import { deviceApi } from '@api/devices'
import DeviceStatusTag from '@components/DeviceStatusTag'
import { formatTime, formatHandshakeId } from '@utils/format'
import styles from './index.module.css'



const DeviceDetail: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [configForm] = Form.useForm()
  const [isMobile, setIsMobile] = useState(false)

  // 监听窗口大小变化
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const { data: device, isLoading } = useQuery({
    queryKey: ['device', deviceId],
    queryFn: () => deviceApi.getDevice(deviceId!),
    enabled: !!deviceId,
  })

  const { data: logsData } = useQuery({
    queryKey: ['device-logs', deviceId],
    queryFn: () => deviceApi.getDeviceLogs(deviceId!, { limit: 50 }),
    enabled: !!deviceId,
  })

  const { data: upgradeStatus } = useQuery({
    queryKey: ['device-ota-status', deviceId],
    queryFn: () => deviceApi.getUpgradeStatus(deviceId!),
    enabled: !!deviceId,
  })

  const { mutate: sendCommand } = useMutation({
    mutationFn: ({ action, payload }: { action: string; payload?: any }) =>
      deviceApi.sendCommand(deviceId!, action, payload),
    onSuccess: () => message.success('命令已发送'),
  })

  const { mutate: upgradeDevice } = useMutation({
    mutationFn: () => deviceApi.upgradeDevice(deviceId!),
    onSuccess: (data) => {
      if (data.upgrade_started) {
        message.success('OTA升级已启动')
      } else {
        message.info(data.message)
      }
      queryClient.invalidateQueries({ queryKey: ['device-ota-status', deviceId] })
    },
  })

  const { mutate: updateConfig } = useMutation({
    mutationFn: (config: any) => deviceApi.updateDeviceConfig(deviceId!, config),
    onSuccess: () => {
      message.success('配置已下发')
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
    },
  })

  const { mutate: setAutoUpgrade } = useMutation({
    mutationFn: (enabled: boolean) => deviceApi.setAutoUpgrade(deviceId!, enabled),
    onSuccess: (data) => {
      message.success(`自动升级已${data.auto_upgrade_enabled ? '开启' : '关闭'}`)
      queryClient.invalidateQueries({ queryKey: ['device', deviceId] })
    },
  })

  const handleConfigSubmit = (values: any) => {
    // 只允许修改 handshake_id_a 和 handshake_id_b，并确保它们是字符串格式
    const config: Record<string, string> = {}
    
    if (values.handshake_id_a !== undefined && values.handshake_id_a !== '') {
      // 转换为字符串并格式化为两位数字格式，如 "00"
      const numValue = Number(values.handshake_id_a)
      config.handshake_id_a = numValue.toString().padStart(2, '0')
    }
    
    if (values.handshake_id_b !== undefined && values.handshake_id_b !== '') {
      // 转换为字符串并格式化为两位数字格式，如 "09"
      const numValue = Number(values.handshake_id_b)
      config.handshake_id_b = numValue.toString().padStart(2, '0')
    }
    
    if (Object.keys(config).length === 0) {
      message.warning('请至少输入一个握手ID')
      return
    }
    
    updateConfig(config)
  }

  if (isLoading) {
    return <div>加载中...</div>
  }

  if (!device) {
    return <div>设备不存在</div>
  }

  const logColumns = [
    { title: '时间', dataIndex: 'timestamp', render: (t: string) => formatTime(t), width: 180 },
    { title: '类型', dataIndex: 'log_type', render: (t: string) => <Tag color={t === 'error' ? 'error' : t === 'warning' ? 'warning' : 'default'}>{t}</Tag>, width: 100 },
    { title: '内容', dataIndex: ['log_data', 'message'], ellipsis: true },
  ]

  const otaColumns = [
    { title: '起始版本', dataIndex: 'from_version', render: (v: string | null) => v || '-' },
    { title: '目标版本', dataIndex: 'to_version', render: (v: string | null) => v || '-' },
    { title: '状态', dataIndex: 'status', render: (s: string) => {
      const colorMap: Record<string, string> = {
        success: 'success',
        failed: 'error',
        downloading: 'processing',
        applying: 'warning',
      }
      const textMap: Record<string, string> = {
        success: '成功',
        failed: '失败',
        downloading: '下载中',
        applying: '升级中',
        pending: '等待中',
        idle: '空闲',
      }
      return <Tag color={colorMap[s] || 'default'}>{textMap[s] || s}</Tag>
    }},
    { title: '消息', dataIndex: 'message', ellipsis: true },
    { title: '开始时间', dataIndex: 'started_at', render: (t: string) => formatTime(t) },
    { title: '完成时间', dataIndex: 'completed_at', render: (t: string | null) => formatTime(t) },
  ]

  const items: TabsProps['items'] = [
    {
      key: 'info',
      label: '基本信息',
      children: (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Card title="设备状态" extra={
            isMobile ? (
              <Space wrap size="small">
                <Button icon={<ReloadOutlined />} onClick={() => sendCommand({ action: 'get_status' })} size="small" />
                <Button icon={<SettingOutlined />} onClick={() => sendCommand({ action: 'get_config' })} size="small" />
                <Popconfirm title="确定重启设备吗？" onConfirm={() => sendCommand({ action: 'reboot' })}>
                  <Button icon={<PoweroffOutlined />} danger size="small" />
                </Popconfirm>
                <Button icon={<CloudUploadOutlined />} type="primary" onClick={() => upgradeDevice()} size="small" />
              </Space>
            ) : (
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => sendCommand({ action: 'get_status' })}>获取状态</Button>
                <Button icon={<SettingOutlined />} onClick={() => sendCommand({ action: 'get_config' })}>获取配置</Button>
                <Popconfirm title="确定重启设备吗？" onConfirm={() => sendCommand({ action: 'reboot' })}>
                  <Button icon={<PoweroffOutlined />} danger>重启</Button>
                </Popconfirm>
                <Button icon={<CloudUploadOutlined />} type="primary" onClick={() => upgradeDevice()}>OTA升级</Button>
              </Space>
            )
          }>
            <Descriptions bordered column={isMobile ? 1 : 2}>
              <Descriptions.Item label="设备ID">{device.device_id}</Descriptions.Item>
              <Descriptions.Item label="在线状态">
                <DeviceStatusTag isOnline={device.is_online} />
              </Descriptions.Item>
              <Descriptions.Item label="固件版本">{device.firmware_version || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="目标版本">{device.target_version || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="握手ID">
                {formatHandshakeId(device.config_data?.handshake_id_a, device.config_data?.handshake_id_b)}
              </Descriptions.Item>
              <Descriptions.Item label="DTU状态">{device.heartbeat_data?.dtu_state || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="信号强度">{device.heartbeat_data?.rssi || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="剩余内存">{device.heartbeat_data?.heap_free || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="TCP连接">{device.heartbeat_data?.dtu_tcp_connected ? '已连接' : '未连接'}</Descriptions.Item>
              <Descriptions.Item label="最后在线">{formatTime(device.last_seen)}</Descriptions.Item>
              <Descriptions.Item label="自动升级">
                <Switch 
                  checked={device.auto_upgrade_enabled} 
                  onChange={(checked) => setAutoUpgrade(checked)}
                />
              </Descriptions.Item>
              <Descriptions.Item label="升级状态">
                {device.upgrade_status === 'idle' ? '空闲' : device.upgrade_status}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="心跳数据">
            <pre>{JSON.stringify(device.heartbeat_data, null, 2)}</pre>
          </Card>
        </Space>
      ),
    },
    {
      key: 'config',
      label: isMobile ? '配置' : '设备配置',
      children: (
        <Card title="下发配置">
          <Form
            form={configForm}
            onFinish={handleConfigSubmit}
            layout="vertical"
            initialValues={device.config_data || undefined}
          >
            <Form.Item name="handshake_id_a" label="握手ID A">
              <InputNumber placeholder="32" min={0} max={255} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="handshake_id_b" label="握手ID B">
              <InputNumber placeholder="33" min={0} max={255} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block={isMobile}>下发配置</Button>
            </Form.Item>
          </Form>
        </Card>
      ),
    },
    {
      key: 'logs',
      label: isMobile ? '日志' : '设备日志',
      children: (
        <Table
          dataSource={logsData?.logs || []}
          columns={logColumns}
          rowKey="id"
          pagination={{ pageSize: 20 }}
        />
      ),
    },
    {
      key: 'ota',
      label: isMobile ? 'OTA' : 'OTA记录',
      children: (
        <>
          <Card title="当前升级状态" style={{ marginBottom: 16 }}>
            <Descriptions>
              <Descriptions.Item label="当前版本">{upgradeStatus?.firmware_version || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="目标版本">{upgradeStatus?.target_version || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="升级状态">{upgradeStatus?.upgrade_status || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="正在升级">{upgradeStatus?.is_upgrading ? '是' : '否'}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="升级历史">
            <Table
              dataSource={upgradeStatus?.recent_records || []}
              columns={otaColumns}
              rowKey="id"
              pagination={false}
            />
          </Card>
        </>
      ),
    },
  ]

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/devices')}>
          返回列表
        </Button>
        <h1>{device.device_id}</h1>
      </div>
      <Tabs items={items} defaultActiveKey="info" />
    </div>
  )
}

export default DeviceDetail
